import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';

interface QuoteItem {
  name?: string;
  product_name?: string;
  description?: string;
  quantity: number;
  unit_price: number;
  total: number;
}

interface Quote {
  id: string;
  title: string;
  description: string;
  items: QuoteItem[];
  total_price: number;
  valid_until: string;
  status: 'draft' | 'sent' | 'accepted' | 'rejected';
  customer_email?: string;
}

export default function PublicQuotePage() {
  const { shareId } = useParams<{ shareId: string }>();
  const navigate = useNavigate();
  const [quote, setQuote] = useState<Quote | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userEmail, setUserEmail] = useState<string>('');
  const [accepting, setAccepting] = useState(false);

  useEffect(() => {
    checkAuth();
    fetchQuote();
  }, [shareId]);

  const checkAuth = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (session?.user?.email) {
      setIsLoggedIn(true);
      setUserEmail(session.user.email);
    }
  };

  const fetchQuote = async () => {
    if (!shareId) {
      setError('Geçersiz teklif bağlantısı');
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/admin-orders`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`,
          },
          body: JSON.stringify({
            action: 'getQuoteByShareId',
            shareId: shareId,
          }),
        }
      );

      const result = await response.json();

      if (!response.ok || result.error) {
        setError('Teklif bulunamadı');
      } else {
        setQuote(result.quote);

        // Giriş sonrası otomatik sepete ekleme kontrolü
        const shouldAddToCart = sessionStorage.getItem('addQuoteToCart');
        if (shouldAddToCart === shareId) {
          const { data: { session } } = await supabase.auth.getSession();
          if (session?.user?.email) {
            sessionStorage.removeItem('addQuoteToCart');
            setIsLoggedIn(true);
            setUserEmail(session.user.email);
            // quote state henüz set edilmediği için result.quote'u direkt kullan
            await addToCartDirect(result.quote, session.user.email);
          }
        }
      }
    } catch (err) {
      console.error('Teklif yüklenirken hata:', err);
      setError('Teklif yüklenirken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const addToCartDirect = async (quoteData: Quote, email: string) => {
    setAccepting(true);
    try {
      const response = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/admin-orders`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`,
          },
          body: JSON.stringify({
            action: 'addQuoteToCart',
            quoteId: quoteData.id,
            customerEmail: email,
          }),
        }
      );

      const result = await response.json();

      if (!response.ok || result.error) {
        alert('Teklif sepete eklenirken bir hata oluştu. Lütfen tekrar deneyin.');
      } else {
        navigate('/cart');
      }
    } catch (err) {
      console.error('Teklif kabul edilirken hata:', err);
      alert('Teklif kabul edilirken bir hata oluştu. Lütfen tekrar deneyin.');
    } finally {
      setAccepting(false);
    }
  };

  const addToCart = async (email: string) => {
    if (!quote) return;
    await addToCartDirect(quote, email);
  };

  const handleAcceptQuote = async () => {
    if (!quote) return;

    if (!isLoggedIn) {
      // Giriş sonrası sepete eklemek için işaretle
      sessionStorage.setItem('addQuoteToCart', shareId || '');
      sessionStorage.setItem('redirectAfterLogin', window.location.pathname);
      navigate('/customer-login');
      return;
    }

    await addToCart(userEmail);
  };

  const isExpired = quote ? new Date(quote.valid_until) < new Date() : false;
  const isAccepted = quote?.status === 'accepted';

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#3366FF]/10 via-white to-[#FF03A7]/10 flex items-center justify-center p-4">
        <div className="text-center">
          <i className="ri-loader-4-line text-5xl text-[#3366FF] animate-spin"></i>
          <p className="text-gray-600 mt-4 text-lg">Teklif yükleniyor...</p>
        </div>
      </div>
    );
  }

  if (error || !quote) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#3366FF]/10 via-white to-[#FF03A7]/10 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-8 text-center">
          <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <i className="ri-error-warning-line text-4xl text-red-600"></i>
          </div>
          <h2 className="text-gray-900 font-bold text-2xl mb-2">Teklif Bulunamadı</h2>
          <p className="text-gray-600 text-sm">{error || 'Bu teklif mevcut değil veya kaldırılmış'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#3366FF]/10 via-[#9933D4]/5 to-[#FF03A7]/10 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Logo */}
        <div className="flex justify-center mb-6">
          <img
            src="https://static.readdy.ai/image/4d025bfd0dded7d3f58fab80d1f8fe1d/1d879113e6ca78ca5c59bde478f0d339.png"
            alt="Kartvizitler Logo"
            className="h-16 object-contain"
          />
        </div>

        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-[#3366FF] via-[#9933D4] to-[#FF03A7] p-8 text-white">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                  <i className="ri-price-tag-3-line text-2xl"></i>
                </div>
                <h1 className="text-white font-bold text-3xl">{quote.title}</h1>
              </div>
              {/* Status Badge */}
              <div>
                {isAccepted ? (
                  <span className="px-4 py-2 bg-green-600 text-white text-sm font-semibold rounded-full whitespace-nowrap">
                    <i className="ri-checkbox-circle-line mr-1"></i>
                    Kabul Edildi
                  </span>
                ) : isExpired ? (
                  <span className="px-4 py-2 bg-red-500 text-white text-sm font-semibold rounded-full whitespace-nowrap">
                    <i className="ri-time-line mr-1"></i>
                    Süresi Doldu
                  </span>
                ) : (
                  <span className="px-4 py-2 bg-white/20 text-white text-sm font-semibold rounded-full whitespace-nowrap">
                    <i className="ri-check-line mr-1"></i>
                    Aktif
                  </span>
                )}
              </div>
            </div>
            {quote.description && (
              <p className="text-white/90 text-sm leading-relaxed">{quote.description}</p>
            )}
          </div>

          {/* Quote Details */}
          <div className="p-8">
            {/* Items Table */}
            <div className="mb-6">
              <h3 className="text-gray-900 font-semibold text-lg mb-4 flex items-center gap-2">
                <i className="ri-file-list-3-line text-[#3366FF]"></i>
                Teklif Kalemleri
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-gradient-to-r from-[#3366FF]/10 to-[#FF03A7]/10 border-b-2 border-[#3366FF]/30">
                      <th className="text-left py-3 px-4 text-gray-700 font-semibold text-sm">Ürün Adı</th>
                      <th className="text-center py-3 px-4 text-gray-700 font-semibold text-sm">Adet</th>
                      <th className="text-right py-3 px-4 text-gray-700 font-semibold text-sm">Birim Fiyat</th>
                      <th className="text-right py-3 px-4 text-gray-700 font-semibold text-sm">Toplam</th>
                    </tr>
                  </thead>
                  <tbody>
                    {quote.items.map((item, index) => (
                      <tr key={index} className="border-b border-gray-100 hover:bg-[#3366FF]/5">
                        <td className="py-4 px-4 text-sm">
                          <span className="text-gray-900 font-medium">{item.name || item.product_name || '-'}</span>
                          {item.description && (
                            <p className="text-gray-500 text-xs mt-1">{item.description}</p>
                          )}
                        </td>
                        <td className="py-4 px-4 text-center text-gray-700 text-sm">{item.quantity}</td>
                        <td className="py-4 px-4 text-right text-gray-700 text-sm">{item.unit_price.toFixed(2)} ₺</td>
                        <td className="py-4 px-4 text-right text-gray-900 font-semibold text-sm">{item.total.toFixed(2)} ₺</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="bg-gradient-to-r from-[#3366FF]/10 to-[#FF03A7]/10 border-t-2 border-[#3366FF]/30">
                      <td colSpan={3} className="py-4 px-4 text-right text-gray-900 font-bold text-base">
                        Genel Toplam:
                      </td>
                      <td className="py-4 px-4 text-right text-[#FF03A7] font-bold text-lg">
                        {quote.total_price.toFixed(2)} ₺
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>

            {/* Valid Until */}
            <div className="bg-gradient-to-r from-[#3366FF]/10 to-[#FF03A7]/10 rounded-xl p-4 mb-6 border border-[#3366FF]/20">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[#3366FF]/15 rounded-full flex items-center justify-center">
                  <i className="ri-calendar-line text-[#3366FF] text-lg"></i>
                </div>
                <div>
                  <p className="text-gray-600 text-xs font-medium">Geçerlilik Tarihi</p>
                  <p className="text-gray-900 font-semibold text-sm">
                    {new Date(quote.valid_until).toLocaleDateString('tr-TR', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                    })}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center mt-8">
          <p className="text-gray-500 text-sm">
            Sorularınız için bizimle iletişime geçebilirsiniz. 💼
          </p>
        </div>
      </div>
    </div>
  );
}
